"use client";

import {
  ChevronLeft,
  ChevronRight,
  CornerDownRight,
  Heart,
  ImageIcon,
  Loader2,
  MessageCircle,
  Mic,
  MoreHorizontal,
  Music2,
  Pin,
  PinOff,
  Send,
  Sticker,
  Trash2,
  X,
} from "lucide-react";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import type { ActivityPostCommentItem } from "@/actions/activity-post";
import type { CommentListItem, FeedPost } from "@/actions/post";
import type { SocialFeedCommentItem } from "@/actions/social-feed-interaction";
import { useAuthPrompt } from "@/components/auth";
import { VoiceNotePreview } from "@/components/chat/composer/voice-note-preview";
import { GifPickerDrawer } from "@/components/chat/pickers/gif-picker-drawer";
import { PostMediaComposer } from "@/components/feed/post-media-composer";
import {
  AssistantAvatar,
  HiiveBotMark,
} from "@/components/mcp/assistant-avatar";
import { MentionTextarea } from "@/components/mentions";
import { MusicAttachmentChip } from "@/components/music/music-attachment-chip";
import { SongCard } from "@/components/music/song-card";
import { SongPickerDrawer } from "@/components/music/song-picker-drawer";
import { CommentLikesSheet } from "@/components/posts/comment-likes-sheet";
import { CommentVoiceNotePlayer } from "@/components/posts/comment-voice-note";
import { ProfileLink } from "@/components/profile/profile-link";
import { HiiveStickerPicker } from "@/components/stickers/hiive-sticker-picker";
import { SharedStickerEmbed } from "@/components/stickers/shared-sticker-embed";
import { AppImage } from "@/components/ui/app-image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { EmptyState } from "@/components/ui/empty-state";
import { HlsVideo } from "@/components/ui/hls-video";
import { ListRowSkeleton } from "@/components/ui/list-row-skeleton";
import { Separator } from "@/components/ui/separator";
import { FloatingSticker } from "@/components/wallet/sticker-card-3d";
import type { SocialFeedEntity } from "@/db/schema/social-feed";
import {
  useVoiceRecorder,
  type VoiceRecordingResult,
} from "@/features/chat/recording/use-voice-recorder";
import {
  useActivityComments,
  useCreateActivityCommentMutation,
  useDeleteActivityCommentMutation,
  useSetActivityCommentPinnedMutation,
  useToggleActivityCommentLikeMutation,
} from "@/hooks/use-activity-comments";
import {
  useActivitySessionComments,
  useCreateActivitySessionCommentMutation,
  useDeleteActivitySessionCommentMutation,
  useSetActivitySessionCommentPinnedMutation,
  useToggleActivitySessionCommentLikeMutation,
} from "@/hooks/use-activity-session-comments";
import {
  useComments,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useSetCommentPinnedMutation,
  useToggleCommentLikeMutation,
} from "@/hooks/use-posts";
import {
  useCreateSocialFeedCommentMutation,
  useDeleteSocialFeedCommentMutation,
  useSetSocialCommentPinnedMutation,
  useSocialFeedComments,
  useToggleSocialFeedCommentLikeMutation,
} from "@/hooks/use-social-feed-interactions";
import { HIIVE_BOT_NAME, isHiiveBotHandle } from "@/lib/assistant-handles";
import { commentMediaFromKlipy } from "@/lib/comments/media";
import {
  getCommentSubtreeIds,
  MAX_COMMENT_DEPTH,
} from "@/lib/comments/threading";
import { formatRelativeTimeSpoken } from "@/lib/dates/relative-time";
import {
  firstMediaPreviewUrl,
  normalizeMediaAsset,
  toMediaAssetSlimForWrite,
} from "@/lib/media";
import { uploadBinary } from "@/lib/media/upload-pipeline";
import {
  mentionColorClass,
  resolveMentionHref,
  segmentTextByMentions,
} from "@/lib/mentions";
import {
  type MusicAttachment,
  musicAttachmentFromSong,
} from "@/lib/music/attachment";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { findSticker } from "@/lib/wallet/sticker-share";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";
import type {
  CommentAssetMedia,
  CommentMedia,
  CommentVoiceNote,
} from "@/validations/comment";

export interface CommentThreadViewer {
  id?: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
}

type EntityType = "post" | "activity" | "activity-session" | "social";

interface CommentThreadBaseProps {
  viewer?: CommentThreadViewer;
  onCommentCountChange?: (count: number) => void;
  targetCommentId?: number | null;
  active?: boolean;
  canModerate?: boolean;
  getAuthorBadge?: (authorId: string) => ReactNode;
  listClassName?: string;
  composerClassName?: string;
  composerPortal?: boolean;
  /** Hide the composer entirely (e.g. while a host drawer is collapsed). */
  composerHidden?: boolean;
  /** Focus the composer once it becomes visible after an expand action. */
  autoFocusComposer?: boolean;
  rootClassName?: string;
  emptyState?: ReactNode;
  loadingState?: ReactNode;
  /**
   * Which comment the thread view is opened on, plus the history behind it.
   * CommentsSheet owns it so its header can go back and retitle; inline
   * surfaces leave it out and the thread keeps its own.
   */
  focus?: CommentFocus;
  /**
   * The thing being commented on, shown at the top of a focused thread so the
   * chain reads post → every ancestor → the comment you're on. Posts derive
   * it from `post`; other entities pass it in.
   */
  threadContext?: CommentThreadContext | null;
  /** Base layer for drawers opened from this comment surface. */
  nestedDrawerLayer?: number;
}

export type CommentThreadContext = {
  author: {
    displayName: string;
    username: string | null;
    avatarUrl: string | null;
  };
  text: string | null;
  createdAt: Date | number;
  thumbnailUrl?: string | null;
};

export type CommentFocus = {
  /** The comment the thread view is on; null = the full comment list. */
  focusedCommentId: number | null;
  /** Drill into a comment, remembering where you came from. */
  open: (commentId: number) => void;
  /** Jump straight to a comment (or the list), forgetting the history. */
  replace: (commentId: number | null) => void;
  /** Return to the previous view — the list once the history runs out. */
  back: () => void;
};

/**
 * Focus history for a comment surface. Back walks the views you actually
 * opened (X's conversation stack), not the comment's ancestry: a deep link
 * straight to a reply goes back to the list, not up its chain.
 */
export function useCommentFocus(): CommentFocus {
  const [stack, setStack] = useState<number[]>([]);
  const open = useCallback(
    (commentId: number) =>
      setStack((current) =>
        current.at(-1) === commentId ? current : [...current, commentId],
      ),
    [],
  );
  const replace = useCallback(
    (commentId: number | null) =>
      setStack((current) => {
        if (commentId == null) return current.length === 0 ? current : [];
        return current.length === 1 && current[0] === commentId
          ? current
          : [commentId];
      }),
    [],
  );
  const back = useCallback(
    () =>
      setStack((current) =>
        current.length === 0 ? current : current.slice(0, -1),
      ),
    [],
  );
  const focusedCommentId = stack.at(-1) ?? null;
  return useMemo(
    () => ({ focusedCommentId, open, replace, back }),
    [focusedCommentId, open, replace, back],
  );
}

interface CommentThreadPostProps extends CommentThreadBaseProps {
  entityType?: "post";
  post: FeedPost;
  entityId?: never;
  socialEntityType?: never;
}

interface CommentThreadActivityProps extends CommentThreadBaseProps {
  entityType: "activity";
  post?: never;
  entityId: number;
  socialEntityType?: never;
}

interface CommentThreadActivitySessionProps extends CommentThreadBaseProps {
  entityType: "activity-session";
  post?: never;
  entityId: number;
  socialEntityType?: never;
}

interface CommentThreadSocialProps extends CommentThreadBaseProps {
  entityType: "social";
  post?: never;
  entityId: number;
  socialEntityType: SocialFeedEntity;
}

export type CommentThreadProps =
  | CommentThreadPostProps
  | CommentThreadActivityProps
  | CommentThreadActivitySessionProps
  | CommentThreadSocialProps;

type UIComment = {
  id: number;
  parentCommentId: number | null;
  rootCommentId: number | null;
  depth: number;
  content: string;
  voice: CommentVoiceNote | null;
  media: CommentMedia | null;
  /** A song reply. */
  music: MusicAttachment | null;
  isPinned: boolean;
  pinnedAt: Date | number | null;
  createdAt: Date | number;
  author: {
    assistantName?: string | null;
    assistantHandle?: string | null;
    id: string;
    displayName: string;
    username: string | null;
    avatarUrl: string | null;
    isBlocked?: boolean;
  };
  likeCount: number;
  isLikedByViewer: boolean;
  tempId?: string;
  isPending?: boolean;
  mentions?: CommentListItem["mentions"];
};

type CommentNode = {
  comment: UIComment;
  children: CommentNode[];
};

const EMOJI_SHORTCUTS = ["❤️", "🙌", "🔥", "👏", "😭", "😍", "😮", "😂"];
/** Reply levels drawn under a focused comment before "N more replies ›". */
const FOCUSED_REPLY_LEVELS = 2;
/** How much of the parent stays visible above a focused comment. */
const FOCUSED_COMMENT_PEEK_PX = 56;

export function CommentThread(props: CommentThreadProps) {
  const {
    viewer,
    onCommentCountChange,
    targetCommentId,
    active = true,
    canModerate = false,
    getAuthorBadge,
    listClassName,
    composerClassName,
    composerPortal = false,
    composerHidden = false,
    autoFocusComposer = false,
    rootClassName,
    emptyState,
    loadingState,
    nestedDrawerLayer = 120,
  } = props;
  const entityType: EntityType = props.entityType ?? "post";
  const entityId = entityType === "post" ? props.post?.id : props.entityId;
  const socialEntityType =
    entityType === "social" ? (props.socialEntityType ?? null) : null;
  const [draft, setDraft] = useState("");
  const [commentMedia, setCommentMediaState] = useState<CommentMedia | null>(
    null,
  );
  // One attachment per comment: a song and a photo/GIF/sticker replace each
  // other, so the composer always shows exactly what will be posted.
  const [commentSong, setCommentSongState] = useState<MusicAttachment | null>(
    null,
  );
  const [songPickerOpen, setSongPickerOpen] = useState(false);
  const setCommentMedia = (media: CommentMedia | null) => {
    setCommentMediaState(media);
    if (media) setCommentSongState(null);
  };
  const setCommentSong = (song: MusicAttachment | null) => {
    setCommentSongState(song);
    if (song) setCommentMediaState(null);
  };
  const [pendingPhoto, setPendingPhoto] = useState<CommentMedia | null>(null);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [photoPickerKey, setPhotoPickerKey] = useState(0);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [gifPickerMode, setGifPickerMode] = useState<"gif" | "sticker" | null>(
    null,
  );
  const [hiiveStickerPickerOpen, setHiiveStickerPickerOpen] = useState(false);
  // An explicit "Reply" on a row other than the focused comment. When it is
  // null the composer replies to whatever the thread view is on — never the
  // post itself while you are looking at a comment.
  const [replyTarget, setReplyTarget] = useState<UIComment | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const internalFocus = useCommentFocus();
  const focus = props.focus ?? internalFocus;
  const isExternallyControlled = props.focus !== undefined;
  const focusedCommentId = focus.focusedCommentId;
  const { open: openFocus, replace: replaceFocus, back: focusBack } = focus;
  const [likesSheetCommentId, setLikesSheetCommentId] = useState<number | null>(
    null,
  );
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const commentListRef = useRef<HTMLDivElement | null>(null);
  const scrolledTargetRef = useRef<number | null>(null);
  const pendingVoiceSendRef = useRef(false);
  const submitCommentRef = useRef<
    (voiceTake?: VoiceRecordingResult | null) => Promise<void>
  >(async () => {});
  const recorder = useVoiceRecorder();
  const { openAuthPrompt } = useAuthPrompt();
  const viewerDisplayName = viewer?.displayName || viewer?.username || "You";
  const isAuthenticated = Boolean(viewer?.id);
  const entityIsOptimistic = entityId != null && entityId < 0;
  const commentsEnabled = active && !entityIsOptimistic;

  const promptForAuth = () => {
    openAuthPrompt({
      title: "Sign in to join the conversation",
      description: "Use your Hiive account to comment, reply, and react.",
    });
  };

  const postCommentsQuery = useComments(
    entityType === "post" ? (entityId ?? null) : null,
    { enabled: commentsEnabled && entityType === "post" },
  );
  const activityCommentsQuery = useActivityComments(
    entityType === "activity" ? (entityId ?? null) : null,
    { enabled: commentsEnabled && entityType === "activity" },
  );
  const activitySessionCommentsQuery = useActivitySessionComments(
    entityType === "activity-session" ? (entityId ?? null) : null,
    { enabled: commentsEnabled && entityType === "activity-session" },
  );
  const socialCommentsQuery = useSocialFeedComments(
    socialEntityType,
    entityType === "social" ? (entityId ?? null) : null,
    { enabled: commentsEnabled && entityType === "social" },
  );
  const commentsQuery =
    entityType === "activity"
      ? activityCommentsQuery
      : entityType === "activity-session"
        ? activitySessionCommentsQuery
        : entityType === "social"
          ? socialCommentsQuery
          : postCommentsQuery;
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch: refetchComments,
  } = commentsQuery;

  const comments = useMemo((): UIComment[] => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.comments.map((raw): UIComment => {
        const comment = raw as (
          | CommentListItem
          | ActivityPostCommentItem
          | SocialFeedCommentItem
        ) & { tempId?: string; isPending?: boolean };
        return {
          id: comment.id,
          parentCommentId: comment.parentCommentId,
          rootCommentId: comment.rootCommentId,
          depth: comment.depth,
          content: comment.content,
          voice: comment.voice,
          media: comment.media,
          music: comment.music ?? null,
          isPinned: comment.isPinned,
          pinnedAt: comment.pinnedAt,
          createdAt: comment.createdAt,
          author: comment.author,
          likeCount: comment.likeCount,
          isLikedByViewer: comment.isLikedByViewer,
          tempId: comment.tempId,
          isPending: comment.isPending,
          mentions:
            entityType === "post" || entityType === "social"
              ? ((comment as CommentListItem | SocialFeedCommentItem)
                  .mentions ?? [])
              : [],
        };
      }),
    );
  }, [data, entityType]);

  const tree = useMemo(() => buildCommentTree(comments), [comments]);
  const sortedRoots = useMemo(() => sortRootComments(tree.roots), [tree.roots]);
  const focusedNode =
    focusedCommentId == null ? null : (tree.byId.get(focusedCommentId) ?? null);
  const focusedComment = focusedNode?.comment ?? null;
  const ancestors = useMemo(
    () => (focusedComment ? collectAncestors(focusedComment, tree.byId) : []),
    [focusedComment, tree.byId],
  );
  const threadContext =
    props.threadContext !== undefined
      ? props.threadContext
      : props.post
        ? threadContextFromPost(props.post)
        : null;
  // Reply to the row you tapped, else to the comment the view is on.
  const composerTarget = replyTarget ?? focusedComment;
  const composerTargetName = composerTarget
    ? composerTarget.author.username
      ? `@${composerTarget.author.username}`
      : composerTarget.author.displayName
    : null;
  const composerTargetAtMaxDepth =
    composerTarget != null && composerTarget.depth >= MAX_COMMENT_DEPTH;
  const pinLabel =
    entityType === "activity-session" ||
    (entityType === "social" && socialEntityType === "event")
      ? "Pinned by host"
      : "Pinned by author";

  // A reply picked in one view never carries into another.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on focus change only.
  useEffect(() => {
    setIsScrolled(false);
    setReplyTarget(null);
  }, [focusedCommentId]);

  // X's conversation view: land on the focused comment with the tail of its
  // parent peeking above, so the chain reads upward. A root (no ancestors)
  // starts at the top so the post it answers is in view.
  const hasAncestors = ancestors.length > 0;
  useEffect(() => {
    if (focusedCommentId == null) return;
    const frame = window.requestAnimationFrame(() => {
      const list = commentListRef.current;
      if (!list) return;
      if (!hasAncestors) {
        list.scrollTop = 0;
        return;
      }
      // Scoped to this list: a hidden Activity route can hold the same id.
      const element = list.querySelector(
        `#comment-${CSS.escape(String(focusedCommentId))}`,
      );
      if (!element) return;
      list.scrollTop +=
        element.getBoundingClientRect().top -
        list.getBoundingClientRect().top -
        FOCUSED_COMMENT_PEEK_PX;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusedCommentId, hasAncestors]);

  useEffect(() => {
    if (!autoFocusComposer || composerHidden || !isAuthenticated) return;

    const frame = window.requestAnimationFrame(() => {
      commentInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocusComposer, composerHidden, isAuthenticated]);

  useEffect(() => {
    if (!active) {
      setDraft("");
      setCommentMediaState(null);
      setCommentSongState(null);
      setSongPickerOpen(false);
      setPendingPhoto(null);
      setPhotoPickerOpen(false);
      setGifPickerMode(null);
      setReplyTarget(null);
      replaceFocus(null);
      scrolledTargetRef.current = null;
      setIsScrolled(false);
      return;
    }
    if (targetCommentId == null || comments.length === 0) return;
    if (scrolledTargetRef.current === targetCommentId) return;
    if (!tree.byId.has(targetCommentId)) return;
    // Open the thread ON the target: its ancestors stack above it and the
    // composer answers it, exactly as if the reader had tapped through.
    replaceFocus(targetCommentId);
    scrolledTargetRef.current = targetCommentId;
  }, [active, targetCommentId, comments, tree.byId, replaceFocus]);

  // A deep link can target a root outside the first page. Advance pagination
  // only while resolving that explicit target; normal browsing stays lazy.
  useEffect(() => {
    if (
      targetCommentId == null ||
      tree.byId.has(targetCommentId) ||
      !hasNextPage ||
      isFetchingNextPage
    ) {
      return;
    }
    void fetchNextPage();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    targetCommentId,
    tree.byId,
  ]);

  // Every page is loaded and the target is still missing: the cached list
  // predates the comment (it can be inside the 15s fresh window, or restored
  // with a hidden route). Refetch once per target rather than leave the
  // notification pointing at nothing.
  const refetchedForTargetRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      !commentsEnabled ||
      targetCommentId == null ||
      !data ||
      isFetching ||
      hasNextPage ||
      tree.byId.has(targetCommentId) ||
      refetchedForTargetRef.current === targetCommentId
    ) {
      return;
    }
    refetchedForTargetRef.current = targetCommentId;
    void refetchComments();
  }, [
    commentsEnabled,
    data,
    hasNextPage,
    isFetching,
    refetchComments,
    targetCommentId,
    tree.byId,
  ]);

  // The focused comment was deleted (by you, or a cascade from an ancestor):
  // step back to the view you came from.
  useEffect(() => {
    if (data && focusedCommentId != null && !tree.byId.has(focusedCommentId)) {
      focusBack();
    }
  }, [data, focusedCommentId, tree.byId, focusBack]);

  useEffect(() => {
    if (!error) return;
    toast.error(
      error instanceof Error ? error.message : "Failed to load comments",
    );
  }, [error]);

  // Tapping send while still recording stops the take first; submit it as
  // soon as the recorder finalizes instead of demanding a second tap.
  useEffect(() => {
    if (!pendingVoiceSendRef.current) return;
    if (recorder.state === "preview" && recorder.result) {
      pendingVoiceSendRef.current = false;
      void submitCommentRef.current(recorder.result);
    } else if (recorder.state === "idle") {
      pendingVoiceSendRef.current = false;
    }
  }, [recorder.state, recorder.result]);

  const postCreateMutation = useCreateCommentMutation();
  const postDeleteMutation = useDeleteCommentMutation();
  const postToggleLikeMutation = useToggleCommentLikeMutation();
  const postPinMutation = useSetCommentPinnedMutation();
  const activityCreateMutation = useCreateActivityCommentMutation();
  const activityDeleteMutation = useDeleteActivityCommentMutation();
  const activityToggleLikeMutation = useToggleActivityCommentLikeMutation();
  const activityPinMutation = useSetActivityCommentPinnedMutation();
  const activitySessionCreateMutation =
    useCreateActivitySessionCommentMutation();
  const activitySessionDeleteMutation =
    useDeleteActivitySessionCommentMutation();
  const activitySessionToggleLikeMutation =
    useToggleActivitySessionCommentLikeMutation();
  const activitySessionPinMutation =
    useSetActivitySessionCommentPinnedMutation();
  const socialCreateMutation = useCreateSocialFeedCommentMutation();
  const socialDeleteMutation = useDeleteSocialFeedCommentMutation();
  const socialToggleLikeMutation = useToggleSocialFeedCommentLikeMutation();
  const socialPinMutation = useSetSocialCommentPinnedMutation();

  const createMutationPending =
    entityType === "activity"
      ? activityCreateMutation.isPending
      : entityType === "activity-session"
        ? activitySessionCreateMutation.isPending
        : entityType === "social"
          ? socialCreateMutation.isPending
          : postCreateMutation.isPending;

  const submitComment = async (voiceTake = recorder.result) => {
    const content = draft.trim();
    if ((!content && !voiceTake && !commentMedia && !commentSong) || !entityId)
      return;
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }
    if (composerTargetAtMaxDepth) return;

    let voice: CommentVoiceNote | null = null;
    if (voiceTake) {
      setIsUploadingVoice(true);
      try {
        const uploaded = await uploadBinary({
          file: voiceTake.file,
          mimeType: voiceTake.file.type,
          scope: {
            domain: "posts",
            slot: "comment-voice",
            entityRef: `${entityType}_${entityId}`,
          },
          draftId: `comment_${Date.now()}`,
        });
        voice = {
          asset: { id: uploaded.assetId, key: uploaded.key, order: 0 },
          durationMs: voiceTake.durationMs,
          waveform: voiceTake.waveform,
        };
      } catch (uploadError) {
        toast.error(
          uploadError instanceof Error
            ? uploadError.message
            : "Unable to upload voice reply",
        );
        setIsUploadingVoice(false);
        return;
      }
      setIsUploadingVoice(false);
    }

    const target = composerTarget;
    const base = {
      content,
      parentCommentId: target?.id ?? null,
      voice,
      media: commentMedia,
      song: commentSong,
      viewer: viewer
        ? {
            id: viewer.id,
            displayName: viewer.displayName,
            username: viewer.username,
            avatarUrl: viewer.avatarUrl,
          }
        : undefined,
    };
    const mutationOptions = {
      onSuccess: () => onCommentCountChange?.(comments.length + 1),
      onError: (mutationError: Error) =>
        toast.error(mutationError.message || "Unable to post comment"),
    };

    if (entityType === "activity-session") {
      activitySessionCreateMutation.mutate(
        { ...base, activitySessionId: entityId },
        mutationOptions,
      );
    } else if (entityType === "social" && socialEntityType) {
      socialCreateMutation.mutate(
        { ...base, entityType: socialEntityType, entityId },
        mutationOptions,
      );
    } else if (entityType === "activity") {
      activityCreateMutation.mutate(
        { ...base, activityPostId: entityId },
        mutationOptions,
      );
    } else {
      postCreateMutation.mutate({ ...base, postId: entityId }, mutationOptions);
    }

    setDraft("");
    setCommentMedia(null);
    setCommentSong(null);
    setReplyTarget(null);
    if (voiceTake) recorder.discard();
    // A reply to the deepest row on screen would land behind "more replies";
    // follow it so you see what you just posted.
    if (
      target &&
      focusedCommentId != null &&
      relativeDepth(target, focusedCommentId, tree.byId) >= FOCUSED_REPLY_LEVELS
    ) {
      openFocus(target.id);
    }
  };
  submitCommentRef.current = submitComment;

  const handleDelete = (commentId: number) => {
    if (!entityId) return;
    if (!isAuthenticated) return promptForAuth();
    const removedCount = getCommentSubtreeIds(comments, commentId).size;
    const options = {
      onSuccess: () =>
        onCommentCountChange?.(
          Math.max(0, comments.length - Math.max(1, removedCount)),
        ),
      onError: (mutationError: Error) => toast.error(mutationError.message),
    };
    if (entityType === "activity-session") {
      activitySessionDeleteMutation.mutate(
        { id: commentId, activitySessionId: entityId },
        options,
      );
    } else if (entityType === "social" && socialEntityType) {
      socialDeleteMutation.mutate(
        { id: commentId, entityType: socialEntityType, entityId },
        options,
      );
    } else if (entityType === "activity") {
      activityDeleteMutation.mutate(
        { id: commentId, activityPostId: entityId },
        options,
      );
    } else {
      postDeleteMutation.mutate({ id: commentId, postId: entityId }, options);
    }
  };

  const handleToggleLike = (comment: UIComment) => {
    if (!entityId) return;
    if (!isAuthenticated) return promptForAuth();
    uiHaptics.like();
    const base = {
      commentId: comment.id,
      currentlyLiked: comment.isLikedByViewer,
    };
    const options = {
      onError: (mutationError: Error) => toast.error(mutationError.message),
    };
    if (entityType === "activity-session") {
      activitySessionToggleLikeMutation.mutate(
        { ...base, activitySessionId: entityId },
        options,
      );
    } else if (entityType === "social" && socialEntityType) {
      socialToggleLikeMutation.mutate(
        { ...base, entityType: socialEntityType, entityId },
        options,
      );
    } else if (entityType === "activity") {
      activityToggleLikeMutation.mutate(
        { ...base, activityPostId: entityId },
        options,
      );
    } else {
      postToggleLikeMutation.mutate({ ...base, postId: entityId }, options);
    }
  };

  const handlePin = (comment: UIComment) => {
    if (!entityId) return;
    const isPinned = !comment.isPinned;
    const options = {
      onSuccess: () =>
        toast.success(isPinned ? "Comment pinned" : "Comment unpinned"),
      onError: (mutationError: Error) => toast.error(mutationError.message),
    };
    if (entityType === "activity-session") {
      activitySessionPinMutation.mutate(
        { activitySessionId: entityId, commentId: comment.id, isPinned },
        options,
      );
    } else if (entityType === "social" && socialEntityType) {
      socialPinMutation.mutate(
        {
          entityType: socialEntityType,
          entityId,
          commentId: comment.id,
          isPinned,
        },
        options,
      );
    } else if (entityType === "activity") {
      activityPinMutation.mutate(
        { activityPostId: entityId, commentId: comment.id, isPinned },
        options,
      );
    } else {
      postPinMutation.mutate(
        { postId: entityId, commentId: comment.id, isPinned },
        options,
      );
    }
  };

  const handleReply = (comment: UIComment) => {
    if (!isAuthenticated) return promptForAuth();
    if (comment.isPending || comment.depth >= MAX_COMMENT_DEPTH) return;
    const isOnScreenBelowFocus =
      focusedCommentId != null &&
      relativeDepth(comment, focusedCommentId, tree.byId) >= 0;
    if (isOnScreenBelowFocus) {
      // The focused comment is the default target; anything under it is an
      // explicit pick the composer chip can cancel back from.
      setReplyTarget(comment.id === focusedCommentId ? null : comment);
    } else {
      // From the list or an ancestor: open that comment's thread so the reply
      // lands where you can see it, with the composer already pointed at it.
      openFocus(comment.id);
    }
    requestAnimationFrame(() => {
      commentInputRef.current?.focus();
    });
  };

  const showLikes = (commentId: number) => {
    if (!isAuthenticated) return promptForAuth();
    setLikesSheetCommentId(commentId);
  };
  const isLoadingInitial = isLoading && comments.length === 0;
  const showEmptyState = !isLoadingInitial && comments.length === 0;
  const voiceSurfaceOpen = recorder.state !== "idle";
  const disableSubmit =
    entityIsOptimistic ||
    createMutationPending ||
    isUploadingVoice ||
    isUploadingPhoto ||
    (!draft.trim() && !recorder.result && !commentMedia && !commentSong) ||
    composerTargetAtMaxDepth ||
    !isAuthenticated;

  const rowProps = (comment: UIComment, isRoot = false) => ({
    comment,
    // The row an explicit Reply points at, so you can see what you answer.
    highlighted: replyTarget?.id === comment.id,
    onOpen:
      comment.isPending || comment.id === focusedCommentId
        ? undefined
        : () => openFocus(comment.id),
    onToggleLike: () => handleToggleLike(comment),
    onShowLikes: () => showLikes(comment.id),
    onDelete: () => handleDelete(comment.id),
    onReply:
      !comment.isPending && comment.depth < MAX_COMMENT_DEPTH
        ? () => handleReply(comment)
        : undefined,
    onPin: canModerate && isRoot ? () => handlePin(comment) : undefined,
    viewerId: viewer?.id,
    authorBadge: getAuthorBadge?.(comment.author.id),
    sheetLayer: nestedDrawerLayer,
  });

  const composer = (
    <div className={cn("shrink-0 bg-background", composerClassName)}>
      <Separator className="bg-border" />
      <div className="space-y-2 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {voiceSurfaceOpen ? (
          <VoiceNotePreview
            mode={recorder.state === "preview" ? "preview" : "recording"}
            previewUrl={recorder.result?.previewUrl}
            durationMs={
              recorder.state === "preview"
                ? (recorder.result?.durationMs ?? 0)
                : recorder.elapsedMs
            }
            waveform={
              recorder.state === "preview"
                ? (recorder.result?.waveform ?? [])
                : recorder.levels
            }
            isPaused={recorder.state === "paused"}
            onDiscard={() => {
              if (recorder.state === "preview") recorder.discard();
              else recorder.cancel();
            }}
            onTogglePause={() => {
              if (recorder.state === "paused") recorder.resume();
              else recorder.pause();
            }}
            onSend={() => {
              if (recorder.result) {
                void submitComment(recorder.result);
              } else {
                pendingVoiceSendRef.current = true;
                recorder.stop();
              }
            }}
            isSending={isUploadingVoice || createMutationPending}
          />
        ) : (
          <>
            <div className="no-scrollbar flex items-center justify-between gap-4 overflow-x-auto">
              {EMOJI_SHORTCUTS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="text-2xl"
                  onClick={() => {
                    if (!isAuthenticated) return promptForAuth();
                    setDraft((previous) => `${previous}${emoji}`);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {commentSong ? (
              <MusicAttachmentChip
                music={commentSong}
                onChange={() => setSongPickerOpen(true)}
                onRemove={() => setCommentSong(null)}
              />
            ) : null}
            {commentMedia ? (
              <div className="relative w-fit max-w-[min(16rem,80vw)]">
                <CommentMediaPreview media={commentMedia} composer />
                <button
                  type="button"
                  onClick={() => setCommentMedia(null)}
                  aria-label="Remove comment attachment"
                  className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-surface text-text-primary shadow-md"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : null}
            {composerTarget ? (
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <CornerDownRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 text-text-tertiary"
                />
                <span className="min-w-0 flex-1 truncate">
                  {composerTargetAtMaxDepth ? (
                    "This thread is as deep as it goes"
                  ) : (
                    <>
                      Replying to{" "}
                      <span className="font-semibold text-text-primary">
                        {composerTargetName}
                      </span>
                      {composerTarget.content ? (
                        <span className="text-text-tertiary">
                          {" "}
                          · {composerTarget.content}
                        </span>
                      ) : null}
                    </>
                  )}
                </span>
                {replyTarget ? (
                  // Only an explicit pick cancels; it falls back to the
                  // comment the view is on, keeping whatever you typed.
                  <button
                    type="button"
                    className="shrink-0 font-medium text-text-primary"
                    onClick={() => setReplyTarget(null)}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="flex items-end gap-2">
              <Avatar className="h-9 w-9 border border-border">
                <AvatarImage src={viewer?.avatarUrl ?? undefined} />
                <AvatarFallback className="text-xs text-text-primary">
                  {initials(viewerDisplayName)}
                </AvatarFallback>
              </Avatar>
              <MentionTextarea
                ref={commentInputRef}
                value={draft}
                onChange={(nextValue) => {
                  if (!isAuthenticated) return promptForAuth();
                  setDraft(nextValue);
                }}
                placeholder={
                  !isAuthenticated
                    ? "Sign in to comment"
                    : composerTarget
                      ? "Add a reply…"
                      : "Add a comment…"
                }
                disabled={
                  entityIsOptimistic ||
                  createMutationPending ||
                  composerTargetAtMaxDepth
                }
                onFocus={() => {
                  if (!isAuthenticated) {
                    promptForAuth();
                    commentInputRef.current?.blur();
                  }
                }}
                containerClassName="min-w-0 flex-1"
                // text-base (16px) minimum: iOS Safari auto-zooms on focus
                // for anything smaller, which breaks the sheet layout.
                className="h-11 w-full resize-none rounded-2xl border border-border bg-surface px-4 py-3 text-base text-text-primary focus-visible:ring-0"
              />
              {/* Voice + send stacked in a single column so the input keeps the
                  full remaining width. */}
              <div className="flex shrink-0 flex-col gap-1.5">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 rounded-full"
                  onClick={async () => {
                    if (!isAuthenticated) return promptForAuth();
                    const result = await recorder.start();
                    if (!result.ok) {
                      toast.error(
                        result.reason === "permission_denied"
                          ? "Enable microphone access to record a voice reply"
                          : "Voice recording is unavailable on this device",
                      );
                    } else {
                      setCommentMedia(null);
                      setCommentSong(null);
                    }
                  }}
                  aria-label="Record voice comment"
                >
                  <Mic className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  className="h-9 w-9 rounded-full bg-brand text-text-inverse"
                  disabled={disableSubmit}
                  onClick={() => void submitComment(null)}
                  aria-label="Post comment"
                >
                  {createMutationPending || isUploadingVoice ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            {/* Edge to edge: bleeds through the composer's px-4 so the row
                starts at the screen edge and scrolls all the way across. */}
            <div className="-mx-4 no-scrollbar flex items-center gap-1 overflow-x-auto overscroll-x-contain px-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 shrink-0 gap-1.5 rounded-full px-2 text-xs"
                onClick={() => {
                  if (!isAuthenticated) return promptForAuth();
                  setPendingPhoto(null);
                  setPhotoPickerKey((value) => value + 1);
                  setPhotoPickerOpen(true);
                }}
              >
                <ImageIcon className="h-4 w-4" /> Photo
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 shrink-0 gap-1.5 rounded-full px-2 text-xs"
                onClick={() => {
                  if (!isAuthenticated) return promptForAuth();
                  setSongPickerOpen(true);
                }}
              >
                <Music2 className="h-4 w-4" /> Song
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 shrink-0 gap-1.5 rounded-full px-2 text-xs"
                onClick={() => {
                  if (!isAuthenticated) return promptForAuth();
                  setGifPickerMode("gif");
                }}
              >
                <span className="text-[11px] font-bold">GIF</span>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 shrink-0 gap-1.5 rounded-full px-2 text-xs"
                onClick={() => {
                  if (!isAuthenticated) return promptForAuth();
                  setGifPickerMode("sticker");
                }}
              >
                <Sticker className="h-4 w-4" /> Sticker
              </Button>
              {/* Last, so the row reads Photo, Song, GIF, Sticker first. */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 shrink-0 gap-1.5 rounded-full px-2 text-xs"
                onClick={() => {
                  if (!isAuthenticated) return promptForAuth();
                  setHiiveStickerPickerOpen(true);
                }}
              >
                <HiiveLogoIcon
                  width={16}
                  height={16}
                  color="currentColor"
                  aria-hidden="true"
                />
                Hiive sticker
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const content = (
    <>
      <div
        ref={commentListRef}
        onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 0)}
        data-vaul-no-drag={isScrolled ? "" : undefined}
        className={cn(
          "min-h-0 px-4 pb-3",
          showEmptyState && rootClassName && "flex flex-col justify-center",
          listClassName,
        )}
      >
        {entityIsOptimistic ? (
          <div className="flex h-full items-center justify-center text-center text-sm text-text-secondary">
            Comments will appear once the post is live.
          </div>
        ) : isLoadingInitial ? (
          (loadingState ?? (
            <ListRowSkeleton rowCount={4} avatarClassName="h-8 w-8" />
          ))
        ) : showEmptyState ? (
          (emptyState ?? (
            <EmptyState
              icon={MessageCircle}
              title="No comments yet"
              description="Be the first to comment."
              compact
              className="flex-1"
            />
          ))
        ) : focusedNode ? (
          <div>
            {!isExternallyControlled ? (
              <button
                type="button"
                onClick={focusBack}
                className="-mx-1 mb-2 flex items-center gap-2 rounded-full px-2 py-1 text-sm font-semibold"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            ) : null}
            {/* X's conversation view: everything that led here, top-down, on
                one rail — the post, then each ancestor — into the comment
                you're on, then its replies. Every rail sits at x=16px, the
                center of the 32px avatars, so the chain reads as one line. */}
            {threadContext ? (
              <ThreadChainItem>
                <ThreadContextRow
                  context={threadContext}
                  onOpen={() => replaceFocus(null)}
                />
              </ThreadChainItem>
            ) : null}
            {ancestors.map((ancestor) => (
              <ThreadChainItem key={ancestor.tempId ?? ancestor.id}>
                {ancestor.isPinned ? <PinnedLabel label={pinLabel} /> : null}
                <CommentRow
                  {...rowProps(ancestor, ancestor.parentCommentId == null)}
                />
              </ThreadChainItem>
            ))}
            <div className="relative -mx-4 border-y border-border/60 bg-surface-alt px-4 pt-3 pb-3">
              {threadContext || ancestors.length > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-8 h-2.5 w-px bg-border"
                />
              ) : null}
              {focusedNode.comment.isPinned ? (
                <PinnedLabel label={pinLabel} />
              ) : null}
              <div className="relative">
                <CommentRow
                  {...rowProps(
                    focusedNode.comment,
                    focusedNode.comment.parentCommentId == null,
                  )}
                  variant="focused"
                  replyingTo={
                    ancestors.at(-1)?.author ?? threadContext?.author ?? null
                  }
                />
                {focusedNode.children.length > 0 ? (
                  <span
                    aria-hidden="true"
                    className="absolute top-4 -bottom-3 left-4 w-px bg-border"
                  />
                ) : null}
              </div>
            </div>
            {focusedNode.children.length > 0 ? (
              <div className="pl-[5px]">
                <ReplyChildren
                  nodes={focusedNode.children}
                  rowProps={rowProps}
                  onOpen={openFocus}
                />
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-text-tertiary">
                No replies yet
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {sortedRoots.map((node) => {
              const replyCount = countDescendants(node);
              return (
                <div
                  key={node.comment.tempId ?? node.comment.id}
                  className="space-y-2"
                >
                  {node.comment.isPinned ? (
                    <PinnedLabel label={pinLabel} />
                  ) : null}
                  <CommentRow {...rowProps(node.comment, true)} />
                  {replyCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => openFocus(node.comment.id)}
                      className="ml-11 flex items-center gap-1 text-sm font-semibold text-text-primary"
                    >
                      {replyCount.toLocaleString()}{" "}
                      {replyCount === 1 ? "reply" : "replies"}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {hasNextPage && !entityIsOptimistic ? (
          <Button
            variant="ghost"
            size="sm"
            className="mx-auto mt-4 flex w-fit text-text-secondary"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "View more comments"}
          </Button>
        ) : null}
      </div>

      {composerHidden
        ? null
        : composerPortal && typeof document !== "undefined"
          ? createPortal(composer, document.body)
          : composerPortal
            ? null
            : composer}

      <CommentLikesSheet
        entityType={entityType}
        commentId={likesSheetCommentId ?? 0}
        open={likesSheetCommentId !== null}
        onOpenChange={(open) => {
          if (!open) setLikesSheetCommentId(null);
        }}
        currentUserId={viewer?.id}
        socialEntityType={socialEntityType ?? undefined}
        layer={nestedDrawerLayer}
      />

      <SongPickerDrawer
        open={songPickerOpen}
        onOpenChange={setSongPickerOpen}
        nested
        layer={nestedDrawerLayer}
        selectedSongId={commentSong?.songId ?? null}
        title="Reply with a song"
        subtitle="Tap a song to preview, + to add"
        onPick={(song) => setCommentSong(musicAttachmentFromSong(song))}
      />

      <HiiveStickerPicker
        open={hiiveStickerPickerOpen}
        onOpenChange={setHiiveStickerPickerOpen}
        nested
        layer={nestedDrawerLayer}
        selectedId={
          commentMedia?.kind === "hiive_sticker" ? commentMedia.stickerId : null
        }
        onSelect={(stickerId) =>
          setCommentMedia({ kind: "hiive_sticker", stickerId })
        }
      />

      <GifPickerDrawer
        open={gifPickerMode !== null}
        onOpenChange={(open) => {
          if (!open) setGifPickerMode(null);
        }}
        initialTab={gifPickerMode === "sticker" ? "stickers" : "gifs"}
        nested
        layer={nestedDrawerLayer}
        onSelect={(item, kind) => {
          const media = commentMediaFromKlipy(item, kind);
          if (media) setCommentMedia(media);
        }}
      />

      <Drawer open={photoPickerOpen} onOpenChange={setPhotoPickerOpen} nested>
        <DrawerContent
          layer={nestedDrawerLayer}
          className="h-[82vh] rounded-t-3xl bg-background"
        >
          <DrawerHeader className="border-b border-border text-left">
            <DrawerTitle>Add a photo</DrawerTitle>
            <DrawerDescription>
              Choose one photo for this comment. You can crop it before posting.
            </DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <PostMediaComposer
              key={photoPickerKey}
              maxImages={1}
              acceptVideos={false}
              enableTagging={false}
              dialogLayer={nestedDrawerLayer + 20}
              uploadScope={{
                domain: "posts",
                slot: "comment-media",
                entityRef: `${entityType}_${entityId ?? "draft"}`,
              }}
              onUploadingChange={setIsUploadingPhoto}
              onImagesChange={(images) => {
                const ready = images.find(
                  (item) => !item.isUploading && !item.error,
                );
                const asset = ready ? toMediaAssetSlimForWrite(ready, 0) : null;
                setPendingPhoto(asset ? { kind: "photo", asset } : null);
              }}
            />
          </div>
          <div className="border-t border-border px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <Button
              type="button"
              className="w-full rounded-2xl"
              disabled={!pendingPhoto || isUploadingPhoto}
              onClick={() => {
                if (!pendingPhoto) return;
                setCommentMedia(pendingPhoto);
                setPhotoPickerOpen(false);
              }}
            >
              {isUploadingPhoto ? "Preparing photo…" : "Add to comment"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );

  return rootClassName ? (
    <div className={cn("flex min-h-0 flex-col", rootClassName)}>{content}</div>
  ) : (
    content
  );
}

/**
 * Renders a parent's replies as a YouTube-style branch list. The vertical
 * stem sits at x=11px — the center of the parent's 24px avatar directly above
 * — and runs continuously through every sibling (`h-full`) except the last,
 * which stops at its own elbow (`h-6`). Each reply indents 24px (`pl-6`) so
 * the curved elbow reaches from the stem into the reply's avatar. Recursion is
 * self-similar but capped at `FOCUSED_REPLY_LEVELS`: deeper replies collapse
 * into "N more replies ›", which opens that reply as the new focused comment
 * (its ancestors then stack above it) instead of indenting off the screen.
 */
function ReplyChildren({
  nodes,
  rowProps,
  onOpen,
  level = 1,
}: {
  nodes: CommentNode[];
  rowProps: (comment: UIComment, isRoot?: boolean) => CommentRowProps;
  onOpen: (commentId: number) => void;
  level?: number;
}) {
  return (
    <>
      {nodes.map((node, index) => {
        const isLast = index === nodes.length - 1;
        const hasChildren = node.children.length > 0;
        const showChildren = hasChildren && level < FOCUSED_REPLY_LEVELS;
        const hiddenCount =
          hasChildren && !showChildren ? countDescendants(node) : 0;
        return (
          <div
            key={node.comment.tempId ?? node.comment.id}
            className="relative pt-3 pl-6"
          >
            {/* Incoming rail from the parent's avatar. It runs the full height
                of this reply's subtree so the next sibling stays connected;
                the last sibling stops at its own elbow. */}
            <span
              aria-hidden="true"
              className={cn(
                "absolute top-0 left-[11px] w-px bg-border",
                isLast ? "h-[14px]" : "h-full",
              )}
            />
            {/* Curved elbow from that rail into this reply's avatar. */}
            <span
              aria-hidden="true"
              className="absolute top-0 left-[11px] h-6 w-[13px] rounded-bl-[10px] border-b border-l border-border"
            />
            {/* This reply's row plus, when it has its own replies, the rail that
                drops from ITS avatar down to them — bridging the gap that the
                incoming rail can't cover so the whole thread stays unbroken. */}
            <div className="relative">
              <CommentRow {...rowProps(node.comment)} size="sm" />
              {showChildren ? (
                <span
                  aria-hidden="true"
                  className="absolute top-3 bottom-0 left-[11px] w-px bg-border"
                />
              ) : null}
            </div>
            {showChildren ? (
              <ReplyChildren
                nodes={node.children}
                rowProps={rowProps}
                onOpen={onOpen}
                level={level + 1}
              />
            ) : hiddenCount > 0 ? (
              <button
                type="button"
                onClick={() => onOpen(node.comment.id)}
                className="mt-1.5 ml-[34px] flex items-center gap-1 text-xs font-semibold text-text-primary"
              >
                {hiddenCount.toLocaleString()} more{" "}
                {hiddenCount === 1 ? "reply" : "replies"}
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/**
 * One link of the conversation chain above a focused comment. The rail drops
 * from the 32px avatar's center (x=16px) to the bottom of the item, where the
 * next link's avatar — or the focused comment's band — picks it up.
 */
function ThreadChainItem({ children }: { children: ReactNode }) {
  return (
    <div className="relative pb-3">
      <span
        aria-hidden="true"
        className="absolute top-9 bottom-0 left-4 w-px bg-border"
      />
      {children}
    </div>
  );
}

/** The post (or activity) at the head of the chain, compact like X's. */
function ThreadContextRow({
  context,
  onOpen,
}: {
  context: CommentThreadContext;
  onOpen: () => void;
}) {
  const name = context.author.displayName || context.author.username || "";
  return (
    <div className="flex gap-3 text-sm">
      <ProfileLink
        handle={context.author.username}
        className="shrink-0 hover:opacity-80"
      >
        <Avatar className="h-8 w-8 border border-border">
          <AvatarImage src={context.author.avatarUrl ?? undefined} />
          <AvatarFallback className="text-2xs">{initials(name)}</AvatarFallback>
        </Avatar>
      </ProfileLink>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`See all comments on ${name}'s post`}
        className="flex min-w-0 flex-1 gap-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5 text-xs">
            <span className="truncate font-semibold text-text-primary">
              {name}
            </span>
            {context.author.username ? (
              <span className="truncate text-text-tertiary">
                @{context.author.username}
              </span>
            ) : null}
            <span
              className="shrink-0 text-text-tertiary"
              suppressHydrationWarning
            >
              · {formatRelativeTimeSpoken(context.createdAt)}
            </span>
          </span>
          {context.text ? (
            <span className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-text-secondary">
              {context.text}
            </span>
          ) : null}
        </span>
        {context.thumbnailUrl ? (
          <AppImage
            src={context.thumbnailUrl}
            alt=""
            width={88}
            height={88}
            sizes="44px"
            className="h-11 w-11 shrink-0 rounded-lg border border-border object-cover"
            imageContext="comment-media"
          />
        ) : null}
      </button>
    </div>
  );
}

interface CommentRowProps {
  comment: UIComment;
  /** Open this comment as the focused thread (tap on its body). */
  onOpen?: () => void;
  /** "focused": the comment a thread view is on — X's larger focused post. */
  variant?: "default" | "focused";
  /** Who the focused comment answers, for its "Replying to" line. */
  replyingTo?: { displayName: string; username: string | null } | null;
  onToggleLike: () => void;
  onShowLikes?: () => void;
  onDelete: () => void;
  onReply?: () => void;
  onPin?: () => void;
  viewerId?: string;
  highlighted?: boolean;
  authorBadge?: ReactNode;
  /** "sm" is the reply size (24px avatar); "md" the root size (32px). */
  size?: "md" | "sm";
  /** Layer for sheets opened from a row (a song reply's sheet). */
  sheetLayer?: number;
}

function CommentRow({
  comment,
  onOpen,
  variant = "default",
  replyingTo,
  onToggleLike,
  onShowLikes,
  onDelete,
  onReply,
  onPin,
  viewerId,
  highlighted = false,
  authorBadge,
  size = "md",
  sheetLayer,
}: CommentRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isViewerComment = Boolean(viewerId && comment.author.id === viewerId);
  const isAuthorBlocked = Boolean(comment.author.isBlocked);
  const canDelete = isViewerComment && !comment.isPending;
  const hasMenu = Boolean(onPin) || canDelete;
  const avatarSize = size === "sm" ? "h-6 w-6" : "h-8 w-8";
  // A Hiive bot reply is stored under the person who asked, but it is the
  // bot's words: show the bot, not their profile.
  const isHiiveBot = isHiiveBotHandle(comment.author.assistantHandle);
  const handleLabel = isHiiveBot
    ? HIIVE_BOT_NAME
    : comment.author.username
      ? `@${comment.author.username}`
      : comment.author.assistantHandle
        ? `${comment.author.assistantName} · @${comment.author.assistantHandle} · bot`
        : comment.author.displayName;

  return (
    <div
      id={`comment-${comment.id}`}
      className={cn(
        "flex scroll-mt-24 text-sm text-text-primary",
        size === "sm" ? "gap-2.5" : "gap-3",
        // Painted on a pseudo-element so the highlight never moves the avatar
        // off the thread rails.
        highlighted &&
          "relative isolate before:absolute before:-inset-x-2 before:-inset-y-1.5 before:-z-10 before:rounded-xl before:bg-brand/[0.07] before:ring-1 before:ring-brand/30",
      )}
    >
      {isHiiveBot ? (
        <AssistantAvatar className={avatarSize} />
      ) : comment.author.assistantHandle && !isAuthorBlocked ? (
        <AssistantAvatar
          ownerName={comment.author.displayName}
          ownerAvatarUrl={comment.author.avatarUrl}
          className={avatarSize}
        />
      ) : isAuthorBlocked ? (
        <Avatar className={cn("shrink-0 border border-border", avatarSize)}>
          <AvatarFallback className="text-2xs">
            {initials(
              comment.author.displayName || comment.author.username || "",
            )}
          </AvatarFallback>
        </Avatar>
      ) : (
        <ProfileLink
          handle={comment.author.username}
          className="shrink-0 hover:opacity-80"
        >
          <Avatar className={cn("border border-border", avatarSize)}>
            <AvatarImage src={comment.author.avatarUrl ?? undefined} />
            <AvatarFallback className="text-2xs">
              {initials(
                comment.author.displayName || comment.author.username || "",
              )}
            </AvatarFallback>
          </Avatar>
        </ProfileLink>
      )}

      {/* Tapping the body opens this comment's thread; taps on its own
          links, buttons and players stay theirs. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the keyboard path is the row's "N replies" / Reply buttons; this is a touch convenience. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: as above. */}
      <div
        className={cn("min-w-0 flex-1 space-y-1", onOpen && "cursor-pointer")}
        onClick={
          onOpen
            ? (event) => {
                const target = event.target as HTMLElement;
                if (
                  target.closest(
                    "a,button,input,textarea,audio,video,[role=menu],[role=menuitem]",
                  )
                ) {
                  return;
                }
                onOpen();
              }
            : undefined
        }
      >
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {isAuthorBlocked ? (
              <span className="text-xs font-medium text-text-secondary">
                Blocked user
              </span>
            ) : (
              <ProfileLink
                handle={isHiiveBot ? null : comment.author.username}
                className="truncate text-xs font-medium text-text-secondary hover:text-text-primary"
              >
                {handleLabel}
              </ProfileLink>
            )}
            {!isAuthorBlocked && isHiiveBot ? <HiiveBotMark /> : null}
            {!isAuthorBlocked && !isHiiveBot && authorBadge}
            <span
              className="shrink-0 text-xs text-text-tertiary"
              suppressHydrationWarning
            >
              · {formatRelativeTimeSpoken(comment.createdAt)}
            </span>
          </div>
          {hasMenu ? (
            <div className="relative">
              <button
                type="button"
                className="-mr-1 flex h-4 w-4 items-center justify-center rounded-full text-text-secondary"
                aria-label="Comment options"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen ? (
                <>
                  <button
                    type="button"
                    aria-label="Close comment options"
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div
                    role="menu"
                    className="absolute top-8 right-0 z-20 w-44 rounded-2xl border border-border bg-surface p-1.5 shadow-xl"
                  >
                    {onPin ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          onPin();
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-surface-alt"
                      >
                        {comment.isPinned ? (
                          <PinOff className="h-4 w-4" />
                        ) : (
                          <Pin className="h-4 w-4" />
                        )}
                        {comment.isPinned ? "Unpin comment" : "Pin comment"}
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          onDelete();
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-error hover:bg-surface-alt"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        {variant === "focused" && replyingTo ? (
          <p className="text-xs text-text-tertiary">
            Replying to{" "}
            <span className="font-medium text-brand">
              {replyingTo.username
                ? `@${replyingTo.username}`
                : replyingTo.displayName}
            </span>
          </p>
        ) : null}
        {comment.content ? (
          <p
            className={cn(
              "text-text-primary",
              variant === "focused" ? "text-[15px] leading-snug" : "text-sm",
            )}
          >
            <span className="whitespace-pre-wrap">
              {segmentTextByMentions(comment.content, comment.mentions).map(
                (segment, index) => {
                  if (segment.kind !== "mention") {
                    return (
                      // biome-ignore lint/suspicious/noArrayIndexKey: text segments have no stable id.
                      <Fragment key={`comment-${comment.id}-text-${index}`}>
                        {segment.value}
                      </Fragment>
                    );
                  }
                  const key = `comment-${comment.id}-mention-${index}`;
                  const href = resolveMentionHref(segment.mention);
                  const className = cn(
                    "font-semibold",
                    mentionColorClass(segment.mention.type),
                    href && "hover:underline",
                  );
                  return href ? (
                    <BackAwareLink key={key} href={href} className={className}>
                      {segment.value}
                    </BackAwareLink>
                  ) : (
                    <span key={key} className={className}>
                      {segment.value}
                    </span>
                  );
                },
              )}
            </span>
          </p>
        ) : null}
        {comment.voice ? (
          <CommentVoiceNotePlayer voice={comment.voice} />
        ) : null}
        {comment.media?.kind === "hiive_sticker" ? (
          <SharedStickerEmbed
            stickerId={comment.media.stickerId}
            owner={{
              id: comment.author.id,
              name: comment.author.displayName,
            }}
            viewerId={viewerId ?? null}
            width={size === "sm" ? 120 : 140}
            nested={sheetLayer !== undefined}
            layer={sheetLayer}
            className="mt-1"
          />
        ) : comment.media ? (
          <CommentMediaPreview media={comment.media} />
        ) : null}
        {comment.music ? (
          <SongCard
            music={comment.music}
            surface="comment"
            interactive={!comment.isPending}
            sheetLayer={sheetLayer}
            className="mt-1 max-w-[19rem]"
          />
        ) : null}
        {comment.isPending ? (
          <span className="text-2xs text-text-secondary uppercase">
            Posting…
          </span>
        ) : null}

        <div className="flex items-center gap-4 pt-1 text-xs text-text-secondary">
          <span className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onToggleLike}
              className="transition-transform active:scale-125"
              aria-pressed={comment.isLikedByViewer}
            >
              <Heart
                className={cn(
                  "h-4 w-4 transition-colors",
                  comment.isLikedByViewer
                    ? "fill-error text-error"
                    : "text-text-secondary hover:text-text-primary",
                )}
              />
              <span className="sr-only">
                {comment.isLikedByViewer ? "Unlike" : "Like"}
              </span>
            </button>
            {comment.likeCount > 0 ? (
              <button
                type="button"
                onClick={onShowLikes}
                className="font-medium tabular-nums"
                aria-label={`${comment.likeCount.toLocaleString()} likes — see who liked this`}
              >
                {formatCompactCount(comment.likeCount)}
              </button>
            ) : null}
          </span>
          {onReply ? (
            <button
              type="button"
              onClick={onReply}
              className="font-semibold hover:text-text-primary"
            >
              Reply
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CommentMediaPreview({
  media,
  composer = false,
}: {
  media: CommentMedia;
  composer?: boolean;
}) {
  if (media.kind === "hiive_sticker") {
    return <HiiveStickerDraftPreview stickerId={media.stickerId} />;
  }
  return <CommentAssetPreview media={media} composer={composer} />;
}

/** The draft shows the same live card the posted comment will. */
function HiiveStickerDraftPreview({ stickerId }: { stickerId: string }) {
  const sticker = findSticker(stickerId);
  if (!sticker) return null;
  return (
    <div className="-mx-3 -mb-2">
      <FloatingSticker sticker={sticker} width={128} />
    </div>
  );
}

function CommentAssetPreview({
  media,
  composer,
}: {
  media: CommentAssetMedia;
  composer: boolean;
}) {
  const normalized = normalizeMediaAsset(media.asset);
  if (!normalized) return null;

  const width =
    ("width" in media ? media.width : undefined) ?? normalized.width ?? 320;
  const height =
    ("height" in media ? media.height : undefined) ?? normalized.height ?? 240;
  const isSticker = media.kind === "sticker";
  const className = cn(
    "block max-h-72 w-auto max-w-full",
    isSticker ? "object-contain" : "rounded-2xl object-cover",
    composer && "max-h-36",
  );

  if (normalized.kind === "video") {
    return (
      <HlsVideo
        src={normalized.url}
        poster={normalized.poster?.url}
        autoPlay
        loop
        muted
        playsInline
        className={className}
        aria-label={isSticker ? "Sticker" : "GIF"}
      />
    );
  }

  if (media.kind === "photo") {
    return (
      <AppImage
        src={normalized.url}
        alt="Comment photo"
        width={width}
        height={height}
        sizes="(max-width: 640px) 70vw, 320px"
        className={className}
        imageContext="comment-media"
      />
    );
  }

  return (
    // biome-ignore lint/performance/noImgElement: KLIPY animated WebP/GIF must retain animation.
    <img
      src={normalized.url}
      alt={isSticker ? "Sticker" : "GIF"}
      width={width}
      height={height}
      loading="lazy"
      className={className}
    />
  );
}

function PinnedLabel({ label }: { label: string }) {
  return (
    <div className="mb-2 ml-11 flex items-center gap-1.5 text-xs text-text-secondary">
      <Pin className="h-3.5 w-3.5 fill-current" />
      {label}
    </div>
  );
}

function buildCommentTree(comments: UIComment[]) {
  const byId = new Map<number, CommentNode>();
  for (const comment of comments) {
    byId.set(comment.id, { comment, children: [] });
  }
  const roots: CommentNode[] = [];
  for (const node of byId.values()) {
    const parent =
      node.comment.parentCommentId == null
        ? null
        : byId.get(node.comment.parentCommentId);
    if (parent && parent !== node) {
      parent.children.push(node);
    } else if (node.comment.parentCommentId == null) {
      roots.push(node);
    }
  }
  const sortChildren = (node: CommentNode) => {
    node.children.sort(
      (left, right) =>
        new Date(left.comment.createdAt).getTime() -
        new Date(right.comment.createdAt).getTime(),
    );
    for (const child of node.children) sortChildren(child);
  };
  for (const root of roots) sortChildren(root);
  return { roots, byId };
}

function countDescendants(node: CommentNode): number {
  return node.children.reduce(
    (total, child) => total + 1 + countDescendants(child),
    0,
  );
}

function sortRootComments(roots: CommentNode[]) {
  return [...roots].sort((left, right) => {
    if (left.comment.isPinned !== right.comment.isPinned) {
      return left.comment.isPinned ? -1 : 1;
    }
    const score = (node: CommentNode) =>
      node.comment.likeCount * 4 + countDescendants(node) * 2;
    const scoreDelta = score(right) - score(left);
    if (scoreDelta !== 0) return scoreDelta;
    return (
      new Date(right.comment.createdAt).getTime() -
      new Date(left.comment.createdAt).getTime()
    );
  });
}

/** Root-first chain of a comment's cached ancestors (excluding itself). */
function collectAncestors(
  comment: UIComment,
  byId: Map<number, CommentNode>,
): UIComment[] {
  const chain: UIComment[] = [];
  const visited = new Set<number>([comment.id]);
  let parentId = comment.parentCommentId;
  while (parentId != null && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = byId.get(parentId)?.comment;
    if (!parent) break;
    chain.unshift(parent);
    parentId = parent.parentCommentId;
  }
  return chain;
}

/**
 * How many levels below `focusedId` a comment sits (0 = the focused comment
 * itself), or -1 when it is not under it at all.
 */
function relativeDepth(
  comment: UIComment,
  focusedId: number,
  byId: Map<number, CommentNode>,
): number {
  let current: UIComment | undefined = comment;
  let levels = 0;
  while (current) {
    if (current.id === focusedId) return levels;
    if (current.parentCommentId == null || levels > MAX_COMMENT_DEPTH) break;
    current = byId.get(current.parentCommentId)?.comment;
    levels += 1;
  }
  return -1;
}

function threadContextFromPost(post: FeedPost): CommentThreadContext {
  // A plain repost's body IS the original; quote the words people answer.
  const source =
    post.kind === "repost" && post.originalPost ? post.originalPost : post;
  return {
    author: source.author,
    text: source.content ?? source.poll?.question ?? null,
    createdAt: post.createdAt,
    thumbnailUrl: firstMediaPreviewUrl(source.media),
  };
}

const initials = (value: string) => {
  const parts = value.trim().split(" ").filter(Boolean);
  if (parts.length <= 1) return parts[0]?.slice(0, 2).toUpperCase() ?? "";
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
};

const compactCountFormatter = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const formatCompactCount = (count: number) =>
  compactCountFormatter.format(count).replace(/\.0([KMB])$/, "$1");
